/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled, @typescript-eslint/consistent-type-imports
import { css, jsx } from '@emotion/react';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/editor-common/FloatingToolbar/Button';
import Separator from '@atlaskit/editor-common/FloatingToolbar/Separator';
import { messages } from '@atlaskit/editor-common/messages/card';
import { linkToolbarMessages } from '@atlaskit/editor-common/messages/link-toolbar';
import { ArrowKeyNavigationType } from '@atlaskit/editor-common/ui-menu/ArrowKeyNavigationProvider/types';
import { DropdownContainer as UiDropdown } from '@atlaskit/editor-common/ui-menu/DropdownContainer';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import EditIcon from '@atlaskit/icon/core/edit';
import ButtonItem from '@atlaskit/menu/button-item';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { focusEditorView } from '../../pm-plugins/utils';
import { editDatasource } from '../editDatasourceAction';
import type { EditDatasourceToolbarButtonWithCommonProps, EditVariant } from './types';

const dropdownExpandContainer = css({
	margin: `0px ${token('space.negative.050')}`,
});

interface EditToolbarPresentationProps extends EditDatasourceToolbarButtonWithCommonProps {
	datasourceId?: string;
	editVariant: EditVariant;
	extensionKey?: string;
}

const EditToolbarButtonPresentation = ({
	datasourceId,
	currentAppearance,
	editorAnalyticsApi,
	editVariant,
	editorView,
	extensionKey,
	onLinkEditClick,
	intl,
	areAnyNewToolbarFlagsEnabled,
}: EditToolbarPresentationProps): jsx.JSX.Element | null => {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef();

	const toggleOpen = () => setIsOpen((open) => !open);

	const onClose = () => setIsOpen(false);

	const onEditLink = useCallback(() => {
		if (editorView) {
			onLinkEditClick(editorView.state, editorView.dispatch);
			focusEditorView(editorView);
		}
	}, [editorView, onLinkEditClick]);

	const onEditDatasource = useCallback(() => {
		if (editorView && datasourceId) {
			editDatasource(
				datasourceId,
				editorAnalyticsApi,
				currentAppearance,
				extensionKey,
			)(editorView.state, editorView.dispatch);
			focusEditorView(editorView);
		}
	}, [currentAppearance, datasourceId, editorAnalyticsApi, editorView, extensionKey]);

	const icon = areAnyNewToolbarFlagsEnabled ? <EditIcon label="" /> : undefined;

	const tooltipContent = areAnyNewToolbarFlagsEnabled
		? intl.formatMessage(linkToolbarMessages.editLink)
		: undefined;

	switch (editVariant) {
		case 'edit-link': {
			return (
				<Flex gap="space.050">
					<Button
						testId="edit-link"
						onClick={onEditLink}
						icon={icon}
						ariaLabel={
							areAnyNewToolbarFlagsEnabled
								? intl.formatMessage(linkToolbarMessages.editLink)
								: undefined
						}
						tooltipContent={tooltipContent}
						areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
					>
						{!areAnyNewToolbarFlagsEnabled && (
							<FormattedMessage
								// Ignored via go/ees005
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...linkToolbarMessages.editLink}
							/>
						)}
					</Button>
					{!areAnyNewToolbarFlagsEnabled && <Separator areAnyNewToolbarFlagsEnabled={false} />}
				</Flex>
			);
		}
		case 'edit-datasource': {
			return (
				<Flex gap="space.050">
					<Button
						testId="edit-datasource"
						tooltipContent={intl.formatMessage(linkToolbarMessages.editDatasourceStandaloneTooltip)}
						onClick={onEditDatasource}
						areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
					>
						<FormattedMessage
							// Ignored via go/ees005
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...linkToolbarMessages.editDatasourceStandalone}
						/>
					</Button>

					{!areAnyNewToolbarFlagsEnabled && <Separator areAnyNewToolbarFlagsEnabled={false} />}
				</Flex>
			);
		}
		case 'edit-dropdown': {
			const trigger = (
				<Flex gap="space.050">
					<Button
						testId="edit-dropdown-trigger"
						iconAfter={
							<span css={dropdownExpandContainer}>
								<ChevronDownIcon
									label={intl.formatMessage(messages.editDropdownTriggerTitle)}
									size="small"
								/>
							</span>
						}
						onClick={toggleOpen}
						selected={isOpen}
						disabled={false}
						ariaHasPopup
						areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
					>
						<FormattedMessage
							// Ignored via go/ees005
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...messages.editDropdownTriggerTitle}
						/>
					</Button>

					{!areAnyNewToolbarFlagsEnabled && <Separator areAnyNewToolbarFlagsEnabled={false} />}
				</Flex>
			);

			return (
				<Flex ref={containerRef}>
					<UiDropdown
						mountTo={containerRef.current}
						isOpen={isOpen}
						handleClickOutside={onClose}
						handleEscapeKeydown={onClose}
						trigger={trigger}
						scrollableElement={containerRef.current}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						arrowKeyNavigationProviderOptions={{
							type: ArrowKeyNavigationType.MENU,
						}}
					>
						<ButtonItem key="edit.link" onClick={onEditLink} testId="edit-dropdown-edit-link-item">
							<FormattedMessage
								// Ignored via go/ees005
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...messages.editDropdownEditLinkTitle}
							/>
						</ButtonItem>
						<ButtonItem
							key="edit.datasource"
							onClick={onEditDatasource}
							testId="edit-dropdown-edit-datasource-item"
						>
							<FormattedMessage
								// Ignored via go/ees005
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...messages.editDropdownEditDatasourceTitle}
							/>
						</ButtonItem>
					</UiDropdown>
				</Flex>
			);
		}
		case 'none':
		default:
			return null;
	}
};

export default EditToolbarButtonPresentation;
